import { download } from '@/actions/model';
import api from '@/api/api';
import { NetworkError } from '@bimdata/client/api.error';
import { ModelUpload, UploadStatus } from '@/state/types/model-upload';
import { Logger } from '@bimdata/client/logger';
import t from '@/utils/translate';
import { DetailedError, HttpRequest, HttpResponse, Upload } from 'tus-js-client';

export type QueuedUpload = ModelUpload & {
  loaded?: number;
  total: number;
  abort?: (status?: UploadStatus) => Promise<void>;
  stop?: () => void;
  perform?: () => Promise<void>;
};

type ScheduledUpload = Omit<QueuedUpload, 'status' | 'abort' | 'stop' | 'perform'> & {
  status: UploadStatus.SCHEDULED;
  abort: (status?: UploadStatus) => Promise<void>;
  stop: () => void;
  perform: () => Promise<void>;
}

/*
type ModelUploadMeta = {
  name: string;
  section?: string;
  status: UploadStatus;
  progress?: number;
  error?: string;
  uploadId?: string;
  forced?: boolean;
  canRetry?: boolean;
  fingerprint?: string;
  previousUploadSize?: number;
  previousUploadCreationTime?: string;
}

function loadUploads(): Record<string, ModelUpload[]> {
  const data = localStorage.get(StorageKey.PROJECT_UPLOADS) || {};
  for (const project in data) {
    data[project] = (data[project] as ModelUploadMeta[]).map((u) => {
      u.canRetry = false;
      if (u.status != UploadStatus.FAILED) {
        u.status = UploadStatus.FAILED;
        u.error = t('model.hint.upload.interrupted');
      }
      (u as ModelUpload).error = new Error(u.error);
      return u;
    });
  }
  return data;
}

function findAllUploads(): Promise<Array<PreviousUpload>> {
  return queue
    .filter(({ fingerprint }) => !!fingerprint)
    .map(({ previousUploadSize, previousUploadCreationTime, section, name, uploadId }) => ({
      size: previousUploadSize || null,
      creationTime: previousUploadCreationTime!,
      uploadUrl: api.resolve(`/project/${projectId}/model/upload/${uploadId}`),
      metadata: {
        section: section!,
        name,
      }
    }));
}

function findUploadsByFingerprint(fp: string): Promise<Array<PreviousUpload>> {
  return queue
    .filter(({ fingerprint }) => fingerprint === fp)
    .map(({ previousUploadSize, previousUploadCreationTime, section, name, uploadId }) => ({
      size: previousUploadSize || null,
      creationTime: previousUploadCreationTime!,
      uploadUrl: api.resolve(`/project/${projectId}/model/upload/${uploadId}`),
      metadata: {
        section: section!,
        name,
      }
    }));
}
*/

export type Download = {
  id: number;
  controller: AbortController;
  loaded: number;
  total?: number;
};

type ProjectMeta = {
  uploading?: boolean;
  uploads: Map<string, QueuedUpload>; 
  uploadQueue: string[];
  scheduled: ScheduledUpload[];
  downloads: Map<number, Download>
};

export type Processes = Map<string, ProjectMeta>;

export type ProcessesList = Map<string, { uploads: QueuedUpload[]; downloads: Download[] }>;

const RUNNING_STATUSES = [UploadStatus.INPROGRESS, UploadStatus.SCHEDULED];

type EventType = 'block' | 'uploadProgress' | 'downloadProgress';

export type EventHandler = () => void;

export class Background {

  private processes: Processes = new Map();

  private memoizedList: ProcessesList | null;

  private memoizedUploadList: Map<string, QueuedUpload[]> | null;

  private memoizedDownloadList: Map<string, Download[]> | null;

  private needRefreshToken: boolean = false;

  private eventHandlers: Map<EventType, EventHandler[]> = new Map();

  get list(): ProcessesList {
    if (!this.memoizedList) {
      this.memoizedList = new Map();

      if (!this.memoizedUploadList) {
        this.memoizedUploadList = new Map();
      }

      if (!this.memoizedDownloadList) {
        this.memoizedDownloadList = new Map();
      }

      for (const [ project, { uploads, uploadQueue, downloads } ] of this.processes) {
        if (downloads.size) {
          this.memoizedDownloadList.set(project, Array.from(downloads.values()));
        }

        if (uploadQueue.length) {
          this.memoizedUploadList.set(project, uploadQueue.map((id) => uploads.get(id)!));
        }

        if (this.memoizedUploadList.has(project) || this.memoizedDownloadList.has(project)) {
          this.memoizedList.set(project, {
            uploads: this.memoizedUploadList.get(project) || [],
            downloads: this.memoizedDownloadList.get(project) || [],
          });
        }
      }
    }
    return this.memoizedList;
  }

  get downloads(): Map<string, Download[]> {
    if (!this.memoizedDownloadList) {
      this.memoizedDownloadList = new Map();

      for (const [ project, { downloads } ] of this.processes) {
        if (downloads.size) {
          this.memoizedDownloadList.set(project, Array.from(downloads.values()));
        }
      }
    }
    return this.memoizedDownloadList;
  }
  
  get uploads(): Map<string, QueuedUpload[]> {
    if (!this.memoizedUploadList) {
      this.memoizedUploadList = new Map();

      for (const [ project, { uploads, uploadQueue } ] of this.processes) {
        if (uploadQueue.length) {
          this.memoizedUploadList.set(project, uploadQueue.map((id) => {
            const { abort, stop, ...upload } = uploads.get(id)!;
            return upload;
          }));
        }
      }
    }
    return this.memoizedUploadList;
  }

  private set uploadsMemo(v: null) {
    this.memoizedUploadList = v;
    this.memoizedList = v;
  }

  private set downloadsMemo(v: null) {
    this.memoizedDownloadList = v;
    this.memoizedList = v;
  }

  constructor() {
    window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => {
      if (this.isBusy()) {
        e.preventDefault();
        e.stopPropagation();
        e.returnValue = true;
        this.emit('block');
      }
    });
  }

  addEventListener(type: EventType, callback: EventHandler): void {
    if (!this.eventHandlers.has(type)) {
      this.eventHandlers.set(type, []);
    }
    this.eventHandlers.get(type)!.push(callback);
  }

  removeEventListener(type: EventType, callback: EventHandler): void {
    const handlers = this.eventHandlers.get(type);
    if (!handlers) return;
    const hi = handlers.findIndex((h) => h === callback);
    if (hi < 0) return;
    handlers.splice(hi, 1);
  }

  private emit(event: EventType) {
    this.eventHandlers.get(event)?.forEach((h) => h());
  }

  private isBusy(): boolean {
    if (!this.processes.size) return false;
    for (const { downloads, uploads } of this.processes.values()) {
      if (!!downloads.size) return true;
      for (const { status } of uploads.values()) {
        if (RUNNING_STATUSES.includes(status)) {
          return true;
        }
      }
    }
    return false;
  }

  private deleteDownload(project: string, model: number) {
    this.processes.get(project)?.downloads.delete(model);
    if (!this.processes.get(project)?.uploads.size && !this.processes.get(project)?.downloads.size) {
      this.processes.delete(project);
    }
    this.downloadsMemo = null;
    this.emit('downloadProgress');
  }

  async modelDownload(project: string, model: number): Promise<void> {
    const controller = new AbortController();
    const downloadProc: Download = {
      id: model,
      controller,
      loaded: 0,
    };
    if (!this.processes.has(project)) {
      this.processes.set(project, { uploads: new Map(), uploadQueue: [], scheduled: [], downloads: new Map() });
    }
    this.processes.get(project)!.downloads.set(downloadProc.id, downloadProc);

    this.downloadsMemo = null;
    this.emit('downloadProgress');

    const file = await download(project, model, controller, (progress, loaded, total) => {
      downloadProc.loaded = loaded;
      downloadProc.total = total;
      this.downloadsMemo = null;
      this.emit('downloadProgress');
    });
    if (!downloadProc.total) {
      downloadProc.total = downloadProc.loaded;
    }
    downloadProc.loaded = downloadProc.total || 0;
    this.downloadsMemo = null;
    this.emit('downloadProgress');
    if (file) {
      const link = document.createElement('a');
      const url = URL.createObjectURL(file);
      link.href = url;
      link.download = file.name;
      link.style.position = 'absolute';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
    this.deleteDownload(project, model);
  }

  modelDownloadProgress(project: string, model: number): number | undefined {
    const d = this.processes.get(project)?.downloads.get(model);
    if (!d) return;
    return d.total ? Math.round(d.loaded * 100 / d.total) : 0;
  }

  abortModelDownload(project: string, model: number) {
    const process = this.processes.get(project)?.downloads.get(model);
    if (process) {
      process.controller.abort();

    }
    this.deleteDownload(project, model);
  }

  /// Uploads

  getModelUpload(project: string, name: string): QueuedUpload | undefined {
    return this.processes.get(project)?.uploads.get(name);
  }

  addModelUpload(project: string, model: ModelUpload) {
    if (!this.processes.has(project)) {
      this.processes.set(project, { uploads: new Map(), uploadQueue: [], scheduled: [], downloads: new Map() });
    }
    const projectMeta = this.processes.get(project)!;
    if (!projectMeta.uploads.has(model.name)) {
      projectMeta.uploadQueue.push(model.name);
    }
    projectMeta.uploads.set(model.name, {
      ...model,
      loaded: 0,
      total: model.file?.size || 0,
    });
    this.uploadsMemo = null;
  }

  private async scheduleUpload(project: string, u: QueuedUpload): Promise<void> {
    if (u.status !== UploadStatus.PENDING) return;
    u.canRetry = false;
    if (!u.file) {
      u.status = UploadStatus.FAILED;
      u.error = new Error(t('model.hint.upload.stale'));
      this.uploadsMemo = null;
      this.emit('uploadProgress');
      return;
    }
    delete u.error;

    const endpoint = api.resolve(`/project/${project}/model/upload`);

    const schedule = (u: QueuedUpload): void => {
      const projectMeta = this.processes.get(project)!;
      u.status = UploadStatus.SCHEDULED;
      projectMeta.scheduled.push(u as ScheduledUpload);
    };

    return new Promise((resolve, reject) => {
      let resolveUpload: () => void;
      let rejectUpload: (err: Error) => void;
  
      const scheduled = new Promise<void>((rs, rj) => { resolveUpload = rs; rejectUpload = rj; });

      u.perform = (): Promise<void> => {
        if (u.status === UploadStatus.SCHEDULED) {
          u.status = UploadStatus.INPROGRESS;
          u.loaded = 0;
          u.total = u.file?.size || 0;
          this.uploadsMemo = null;
          this.emit('uploadProgress');
          u.upload!.start();
        } else if (u.status === UploadStatus.FAILED) {
          rejectUpload(u.error!);
        } else if (u.status !== UploadStatus.INPROGRESS) {
          resolveUpload();
        }
        return scheduled
      };
  
      try {
        u.upload = new Upload(u.file!, {
          endpoint,
/*          
          urlStorage: {
            findAllUploads,
            findUploadsByFingerprint,
            removeUpload: async (urlStorageKey: string): Promise<void> => {
              delete u.fingerprint;
            },
            addUpload: async (fp: string, { size, creationTime }: PreviousUpload): Promise<string> => {
              u.fingerprint = fp;
              if (typeof size === 'number') {
                u.previousUploadSize = size;
              }
              u.previousUploadCreationTime = creationTime;
              return fp;
            }
          },
*/
          removeFingerprintOnSuccess: true,
          metadata: {
            name: u.name,
            //section: queue[pending].section || defaultSection
            ...(u.section ? { section: u.section } : {})
          },
          onAfterResponse: function (req: HttpRequest, res: HttpResponse): void {
            const method = req.getMethod();
            const status = res.getStatus();
            const location = res.getHeader('location');
            if (method === 'POST' && status === 201 && location) {
              u.uploadId = location.split('/').pop();
              u.upload!.url = `${endpoint}/${u.uploadId}`;

              if (u.status === UploadStatus.SCHEDULED) {
                u.upload!.abort()
                  .then(() => resolve())
                  .catch((err) => Logger.error(err));
              }
            }
          },
          onBeforeRequest: async (req: HttpRequest): Promise<void> => {
            if (this.needRefreshToken) {
              await api.refreshToken();
              this.needRefreshToken = false;
            }
            const headers = api.headers();
            for (const key in api.headers()) {
              req.setHeader(key, headers[key]);
            }
            if (window.GDATA_DUMP_ATTRS) {
              req.setHeader('dumped', '1');
            }
            if (u.forced) {
              req.setHeader('forced', '1');
            }
          },
          onShouldRetry: (error: Error/*, attempt*/): boolean => {
            if (error instanceof DetailedError && error.originalResponse?.getStatus() === 401) {
              this.needRefreshToken = true;
              return true;
            }
            return false;
          },
          onError: (error: Error): void => {
            if (u.status === UploadStatus.FAILED || u.status === UploadStatus.SUCCESS) {
              return;
            }
            if (error instanceof DetailedError) {
              if (error.originalResponse) {
                switch (error.originalResponse.getStatus()) {
                  case 449: {
                    const data = JSON.parse(error.originalResponse.getBody());
                    u.error = data;
                    u.status = UploadStatus.BLOCKED;
                    u.upload!.abort(true);
                    this.uploadsMemo = null;
                    this.emit('uploadProgress');
                    resolveUpload();
                    return;
                  }
                  case 503: {
                    error = new Error(t('error.code503'));
                    u.canRetry = true;
                  } break;
                  default: {
                    const data = JSON.parse(error.originalResponse.getBody());
                    error = new Error(data.message);
                  }
                }
              } else {
                error = new NetworkError(error, t('error.noNetwork'));
                u.canRetry = true;
              }
            }
            u.status = UploadStatus.FAILED;
            u.error = error;
            u.upload!.abort(true).catch((err) => {
              const status: number = (err instanceof DetailedError && err.originalResponse)
                ? (err.originalResponse.getStatus())
                : 500;
              if (status !== 404) {
                Logger.error(err);
              }
            });
            this.uploadsMemo = null;
            this.emit('uploadProgress');
            rejectUpload(u.error);
          },
          onProgress: (bytesUploaded: number, bytesTotal: number): void => {
            u.loaded = bytesUploaded;
            u.total = bytesTotal;
            this.uploadsMemo = null;
            this.emit('uploadProgress');
          },
          onSuccess: (): void => {
            u.status = UploadStatus.SUCCESS;
            this.uploadsMemo = null;
            this.emit('uploadProgress');
            resolveUpload();
          }
        });

        u.abort = async (status?: UploadStatus): Promise<void> => {
          if (u.upload && RUNNING_STATUSES.includes(u.status)) {
            await u.upload.abort(u.status === UploadStatus.SCHEDULED);
          }
          u.status = status || UploadStatus.ABORTED;
          this.uploadsMemo = null;
          resolveUpload();
        };

        u.stop = (): void => {
          this.uploadsMemo = null;
          resolveUpload();
        }

        if (u.loaded) {
          u.upload.findPreviousUploads().then((prev) => {
            if (prev.length) {
              u.status = UploadStatus.INPROGRESS;
              u.upload!.resumeFromPreviousUpload(prev[0]);
              resolve();
            } else {
              schedule(u);
              resolve();
            }
          }).catch((err) => reject(err));
        } else {
          schedule(u);
          resolve();
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  private async launch(project: string, onError: (err: Error) => void) {
    const projectMeta = this.processes.get(project);
    if (!projectMeta) return;

    if (!projectMeta.uploading) {
      projectMeta.uploading = true;
      let scheduled: ScheduledUpload | undefined = undefined;
      // eslint-disable-next-line no-cond-assign
      while (scheduled = projectMeta.scheduled.shift()) {
        try {
          await scheduled.perform();
          this.emit('uploadProgress');
        } catch (err) {
          onError(err);
        }
      }
      projectMeta.uploading = false;
    }
  }  

  async send(project: string, onError: (err: Error) => void) {
    const projectMeta = this.processes.get(project);
    if (!projectMeta) return;
    const queue = projectMeta.uploadQueue.map((nm) => projectMeta.uploads.get(nm)!).filter(({ status }) => status == UploadStatus.PENDING);
    if (queue.length) {
      await Promise.all(queue.map((u) => this.scheduleUpload(project, u)));
      await this.launch(project, onError);
    }
    this.uploadsMemo = null;
    this.emit('uploadProgress');
  }

  private getUpload(project: string, name: string): { projectMeta?: ProjectMeta, upload?: QueuedUpload } {
    const result: { projectMeta?: ProjectMeta, upload?: QueuedUpload } = {};
    const projectMeta = this.processes.get(project);
    if (!projectMeta) return result;
    result.projectMeta = projectMeta;
    const upload = projectMeta.uploads.get(name);
    if (!upload) return result;
    result.upload = upload;
    return result;
  }

  deleteUpload(project: string, item: ModelUpload) {
    const { projectMeta, upload } = this.getUpload(project, item.name);
    if (!upload || !projectMeta) return;

    if (upload.abort) {
      upload.abort();
    }

    projectMeta.uploads.delete(item.name);
    projectMeta.uploadQueue.splice(projectMeta.uploadQueue.findIndex((nm) => nm === item.name), 1);

    if (!projectMeta.uploads.size && !projectMeta.downloads.size) {
      this.processes.delete(project);
    }

    this.uploadsMemo = null;
    this.emit('uploadProgress');
  }

  forceUpload(project: string, item: ModelUpload) {
    const { upload } = this.getUpload(project, item.name);
    if (!upload) return;
    upload.forced = true;
    upload.status = UploadStatus.PENDING;
    this.uploadsMemo = null;
  }

  retryUpload(project: string, item: ModelUpload) {
    const { upload } = this.getUpload(project, item.name);
    if (!upload) return;
    upload.status = UploadStatus.PENDING;
    this.uploadsMemo = null;
  }

  cleanSuccessUploads() {
    this.processes.forEach((meta, project) => {
      const newQ: string[] = [];
      meta.uploadQueue.forEach((nm) => {
        const u = meta.uploads.get(nm)!;
        if (u.status === UploadStatus.SUCCESS) {
          meta.uploads.delete(nm);
        } else {
          newQ.push(nm);
        }
      });
      meta.uploadQueue = newQ;
    });
    this.uploadsMemo = null;
  }
}
